import React from 'react';
import {
  Typography,
  Box,
  Paper,
  Avatar,
  useMediaQuery,
  useTheme
} from '@mui/material';

const InkColorIcon = ({ color }) => {
  const getImagePath = (colorName) => {
    const colorMap = {
      'ruby': '/icons/ink-ruby.webp',
      'sapphire': '/icons/ink-sapphire.webp',
      'amber': '/icons/ink-amber.webp',
      'amethyst': '/icons/ink-amethyst.webp',
      'emerald': '/icons/ink-emerald.webp',
      'steel': '/icons/ink-steel.webp',
    };
    return colorMap[colorName.toLowerCase()] || '/icons/ink-default.png';
  };

  return (
    <Avatar
      src={getImagePath(color)}
      alt={color}
      sx={{
        width: 20,
        height: 20,
        mr: 0.5,
        backgroundColor: 'transparent'
      }}
    />
  );
};

const TournamentResultsTable = ({ results }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (!results || Object.keys(results).length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 2 }}>
        <Typography variant="body1" sx={{ color: 'rgb(196, 181, 253)' }}>
          Wyniki turnieju nie są jeszcze dostępne
        </Typography>
      </Box>
    );
  }

  // Convert and sort results, limit to TOP 8
  const resultsArray = Object.entries(results)
    .map(([place, data]) => ({
      place: parseInt(place),
      ...data
    }))
    .sort((a, b) => a.place - b.place)
    .slice(0, 8);

  const showInkColors = resultsArray.some(result => result.ink_colors?.length > 0);

  return (
    <Paper
      sx={{
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        borderRadius: '0.5rem',
        border: '1px solid rgba(76, 29, 149, 0.3)',
        overflow: 'hidden'
      }}
    >
      <Box sx={{ p: isMobile ? 0.5 : 1 }}>
        {resultsArray.map((result, index) => (
          <Box
            key={result.place}
            sx={{
              display: 'flex',
              alignItems: 'center',
              py: isMobile ? 0.75 : 1.25,
              px: isMobile ? 0.25 : 0.5,
              borderBottom: index < resultsArray.length - 1 ? '1px solid rgba(76, 29, 149, 0.15)' : 'none',
              '&:hover': {
                backgroundColor: 'rgba(76, 29, 149, 0.05)'
              }
            }}
          >
            {/* Miejsce */}
            <Box
              sx={{
                backgroundColor: 'rgba(76, 29, 149, 0.4)',
                borderRadius: '4px',
                width: isMobile ? '28px' : '32px',
                height: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: isMobile ? 1 : 1.5,
                flexShrink: 0
              }}
            >
              <Typography sx={{
                color: 'rgb(196, 181, 253)',
                fontSize: isMobile ? '0.7rem' : '0.75rem',
                fontWeight: 'bold',
                lineHeight: 1
              }}>
                {result.place}
              </Typography>
            </Box>

            {/* Nazwa gracza + inki */}
            <Box sx={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              alignItems: 'center',
              pr: isMobile ? 0.5 : 1,
              overflow: 'hidden'
            }}>
              <Typography
                sx={{
                  color: 'white',
                  fontSize: isMobile ? '0.8rem' : '0.85rem',
                  lineHeight: 1.3,
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  display: '-webkit-box',
                  WebkitLineClamp: 1,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  mr: 0.5
                }}
              >
                {result.player_name}
              </Typography>

              {showInkColors && result.ink_colors && (
                <Box sx={{
                  display: 'flex',
                  flexShrink: 0
                }}>
                  {result.ink_colors.map((color, idx) => (
                    <InkColorIcon key={idx} color={color} />
                  ))}
                </Box>
              )}
            </Box>

            {/* Wynik i punkty - zredukowane paddingi na mobile */}
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0,
              ml: isMobile ? 0.25 : 1, // Zmniejszony margines na mobile
              mr: isMobile ? 0 : 'auto' // Usunięty prawy padding na mobile
            }}>
              <Typography sx={{
                color: 'white',
                fontSize: isMobile ? '0.8rem' : '0.85rem',
                minWidth: isMobile ? '36px' : '50px', // Zmniejszona szerokość na mobile
                textAlign: 'right',
                mr: isMobile ? 0.25 : 1 // Zmniejszony margines na mobile
              }}>
                {result.score}
              </Typography>
              <Typography sx={{
                color: 'rgb(196, 181, 253)',
                fontSize: isMobile ? '0.75rem' : '0.85rem',
                minWidth: isMobile ? '45px' : '60px', // Zmniejszona szerokość na mobile
                textAlign: 'right'
              }}>
                {result.points} pkt
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

export default TournamentResultsTable;